import { ArrowUpRight, FileText, ClipboardSignature, Home, Receipt, Clock } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { useGoTo, useHashScroll } from "@/lib/navigation";

const RESOURCE_LINKS = [
  { id: "ato-agent-nomination", href: "/resources/ato-agent-nomination.html", external: true, Icon: FileText },
  { id: "client-authorisation", href: "/resources/client-authorisation", external: false, Icon: ClipboardSignature },
  { id: "investment-property", href: "/resources/investment-property", external: false, Icon: Home },
  { id: "individual-tax-return", href: "/resources/individual-tax-return", external: false, Icon: Receipt },
];

export default function ResourcesPage() {
  const { t, lang } = useLang();
  const r = t.resources;
  useHashScroll();
  const goTo = useGoTo();

  return (
    <div data-testid="resources-page" className="bg-cream-50 text-ink-900 min-h-screen">
      <Header />
      <main>
        <section className="pt-36 sm:pt-44 pb-24 sm:pb-32 bg-cream-100 relative">
          <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />
          <div className="container-x relative">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-5">
                <span className="hairline" />
                <span className="text-overline">{r.overline}</span>
              </div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-ink-900 tracking-tight leading-[1.05]">
                {r.h1a} <span className="text-brand-500">{r.h1accent}</span> {r.h1b}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-ink-500 leading-relaxed">{r.sub}</p>
            </div>

            <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {r.items.map((it, i) => {
                const { id, href, external, Icon } = RESOURCE_LINKS[i];
                return (
                <a
                  key={id}
                  href={external ? `${href}?lang=${lang}` : href}
                  onClick={external ? undefined : (e) => { e.preventDefault(); goTo(href); }}
                  data-testid={`resource-card-${id}`}
                  className="group bg-white border border-ink-900/8 hover:border-brand-500 hover:-translate-y-1 transition-all duration-500 p-8 sm:p-10 flex flex-col shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 grid place-items-center bg-brand-50 text-brand-600">
                      <Icon className="w-5 h-5" strokeWidth={1.8} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-ink-500/50 group-hover:text-brand-500 group-hover:rotate-12 transition-all duration-300" />
                  </div>
                  <span className="mt-7 text-[11px] uppercase tracking-[0.2em] font-bold text-brand-600">{it.tag}</span>
                  <h2 className="mt-2 font-display font-bold text-xl sm:text-2xl text-ink-900">{it.title}</h2>
                  <p className="mt-3 text-sm sm:text-base text-ink-500 leading-relaxed flex-1">{it.desc}</p>
                  <div className="mt-7 pt-5 border-t border-ink-900/8 flex items-center justify-between gap-4">
                    <span className="flex items-center gap-2 text-xs text-ink-500">
                      <Clock className="w-3.5 h-3.5 text-brand-500" />
                      {it.meta}
                    </span>
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-500">{it.cta || r.open} →</span>
                  </div>
                </a>
                );
              })}

              <div
                data-testid="resources-coming-soon"
                className="border border-dashed border-ink-900/15 p-8 sm:p-10 flex flex-col justify-center"
              >
                <h2 className="font-display font-bold text-xl text-ink-900/70">{r.comingSoon}</h2>
                <p className="mt-3 text-sm text-ink-500 leading-relaxed">{r.comingSoonSub}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
